import { useCallback, useState } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { X } from "lucide-react";
import {
  CustomDialog,
  StyledButton,
  InitialNewProjectFormState,
} from "../config";
import {
  BasicCompanyInfoSection,
  ProductServiceDetailsSection,
  MarketPositionSection,
  CustomerSuccessSection,
  BusinessGoalsSection,
  BudgetResourcesSection,
} from "./FormSections";

const NewProjectDialog = ({ userId, open, onClose, onSubmit }: any) => {
  const [expandedSection, setExpandedSection] = useState("basic");
  const [formData, setFormData] = useState<any>(InitialNewProjectFormState);

  const handleInputChange = useCallback(
    (section: any, field: any, value: any) => {
      setFormData((prev: any) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    },
    []
  );

  const handleArrayInputChange = useCallback(
    (section: any, field: any, value: any) => {
      const arrayValue = value.split(",").map((item: any) => item.trim());
      setFormData((prev: any) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: arrayValue,
        },
      }));
    },
    []
  );

  const handleNestedObjectChange = useCallback(
    (section: any, parentField: any, field: any, value: any) => {
      setFormData((prev: any) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [parentField]: {
            ...prev[section][parentField],
            [field]: value,
          },
        },
      }));
    },
    []
  );

  const handleSubmit = async () => {
    const processedData = {
      ...formData,
      userId,

      // Basic Company Info - convert numeric fields
      basicCompanyInfo: {
        ...formData.basicCompanyInfo,
        companySize: Number(formData.basicCompanyInfo.companySize),
        yearsInBusiness: Number(formData.basicCompanyInfo.yearsInBusiness),
        geographicLocations: Array.isArray(
          formData.basicCompanyInfo.geographicLocations
        )
          ? formData.basicCompanyInfo.geographicLocations
          : [],
      },

      // Product Service Details - ensure arrays
      productServiceDetails: {
        ...formData.productServiceDetails,
        keyFeaturesBenefits: Array.isArray(
          formData.productServiceDetails.keyFeaturesBenefits
        )
          ? formData.productServiceDetails.keyFeaturesBenefits
          : [],
      },

      // Market Position - convert numeric fields and ensure arrays
      currentMarketPosition: {
        ...formData.currentMarketPosition,
        mainCompetitors: Array.isArray(
          formData.currentMarketPosition.mainCompetitors
        )
          ? formData.currentMarketPosition.mainCompetitors
          : [],
      },

      // Customer Success Metrics - ensure arrays
      customerSuccessMetrics: {
        ...formData.customerSuccessMetrics,
        commonPainPointsSolved: Array.isArray(
          formData.customerSuccessMetrics.commonPainPointsSolved
        )
          ? formData.customerSuccessMetrics.commonPainPointsSolved
          : [],
      },

      // Business Goals - ensure arrays
      businessGoals: {
        ...formData.businessGoals,
      },

      // Budget Resources - all strings, no processing needed
      budgetResourceAllocation: {
        ...formData.budgetResourceAllocation,
      },
    };

    await onSubmit(processedData);
    setFormData(InitialNewProjectFormState);
    onClose();
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      scroll="paper"
      PaperProps={{
        style: {
          borderRadius: "16px",
          maxWidth: "800px",
          background: "linear-gradient(to bottom, #ffffff, #f9fafb)",
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: "24px 24px 8px 24px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(229, 231, 235, 0.5)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            background: "linear-gradient(to right, #ef4444, #b91c1c)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: "bold",
          }}
        >
          New Project Setup
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: "rgba(243, 244, 246, 0.8)",
            "&:hover": {
              backgroundColor: "rgba(229, 231, 235, 0.8)",
            },
            padding: "8px",
            borderRadius: "12px",
          }}
        >
          <X className="h-5 w-5 text-gray-600" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "16px 24px" }}>
        <Box sx={{ mt: 2, mb: 4, textAlign: "center" }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: "1rem", marginBottom: "8px" }}
          >
            Complete the following information to help our AI agents deeply
            understand your business and generate accurate ICPs.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#ef4444",
              backgroundColor: "rgba(254, 226, 226, 0.5)",
              padding: "4px 12px",
              borderRadius: "16px",
              display: "inline-block",
              fontWeight: "500",
              fontSize: "0.75rem",
            }}
          >
            ALL FIELDS MUST BE COMPLETED
          </Typography>
        </Box>

        <form className="space-y-6">
          <BasicCompanyInfoSection
            formData={formData}
            handleChange={handleInputChange}
            handleArrayChange={handleArrayInputChange}
            handleNestedChange={handleNestedObjectChange}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
          />
          <ProductServiceDetailsSection
            formData={formData}
            handleChange={handleInputChange}
            handleArrayChange={handleArrayInputChange}
            handleNestedChange={handleNestedObjectChange}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
          />
          <MarketPositionSection
            formData={formData}
            handleChange={handleInputChange}
            handleArrayChange={handleArrayInputChange}
            handleNestedChange={handleNestedObjectChange}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
          />
          <CustomerSuccessSection
            formData={formData}
            handleChange={handleInputChange}
            handleArrayChange={handleArrayInputChange}
            handleNestedChange={handleNestedObjectChange}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
          />
          <BusinessGoalsSection
            formData={formData}
            handleChange={handleInputChange}
            handleArrayChange={handleArrayInputChange}
            handleNestedChange={handleNestedObjectChange}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
          />
          <BudgetResourcesSection
            formData={formData}
            handleChange={handleInputChange}
            handleArrayChange={handleArrayInputChange}
            handleNestedChange={handleNestedObjectChange}
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
          />
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px 24px",
          borderTop: "1px solid rgba(229, 231, 235, 0.5)",
          marginTop: "16px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            borderColor: "rgba(229, 231, 235, 0.8)",
            color: "#6B7280",
            textTransform: "none",
            fontWeight: 500,
            padding: "8px 16px",
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "rgba(243, 244, 246, 0.8)",
            },
          }}
        >
          Cancel
        </Button>
        <StyledButton
          variant="contained"
          onClick={handleSubmit}
          sx={{
            background: "linear-gradient(to right, #ef4444, #dc2626)",
            boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.2)",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            padding: "8px 20px",
            "&:hover": {
              boxShadow: "0 10px 15px -3px rgba(239, 68, 68, 0.3)",
            },
          }}
        >
          Create Project
        </StyledButton>
      </DialogActions>
    </CustomDialog>
  );
};

export default NewProjectDialog;
